import type { TitleRow } from '../provider/providerClient'

/**
 * THE SLICE MODEL — v1's "slice and dice", as pure functions over the titles payload.
 *
 * ★★★ NO NEW ENDPOINTS, BY DESIGN. `GET /provider/titles` already returns the whole catalogue in
 * tree order with `ParentKey`, `Depth`, `IsRollUp`, `EntryType` and per-level ranks, and every
 * roll-up row already carries the SUM of its subtree (see `ProviderAnalyticsService::titles`). So
 * every axis below is a re-cut of data we have already fetched: instant, and impossible to
 * desync from the figures beside it because there is nothing to fetch again.
 *
 * ★★ WHY THIS IS A SEPARATE, PURE FILE. No provider in the real data has BOTH series and films
 * (David, 2026-07-23): Bailey is films + extras, ZDF and All3 are series. So the one case where a
 * grouping bug would actually hide — a mixed catalogue — cannot be reached by signing in as
 * anybody. It is reachable here, in a fixture. A flat fixture cannot show a targeting bug.
 */

/**
 * ★ THE TWO FAMILIES. "Works" are the things a supplier delivered as programming; "supporting
 * material" is what hangs off a work — the trailer, the interview, the written piece.
 *
 * ⚠ DERIVED FROM `EntryType`, NOT FROM POSITION IN THE TREE. An episode has a parent and so does
 * a trailer; only the type says which of them is the programme.
 */
export const WORK_TYPES = ['Film', 'Series', 'Season', 'Episode'] as const
export const SUPPORTING_TYPES = ['Trailer', 'Podcast', 'Article'] as const

export function isSupporting(entryType: string): boolean {
  return (SUPPORTING_TYPES as readonly string[]).includes(entryType)
}

/** The key of the synthetic grouping node under `parentKey` (or at the root when empty). */
export function supportingGroupKey(parentKey: string): string {
  return `supporting:${parentKey}`
}

export type SliceType = 'all' | 'films' | 'series' | 'supporting'
/** Which level the RANKINGS compare at. `title` is v1's "all seasons". */
export type SliceDepth = 'title' | 'season' | 'episode' | 'supporting'
/** What a percentage is a percentage OF. v1 had this as the two top tabs. */
export type SliceScope = 'yours' | 'platform'

export interface SliceAxes {
  type: SliceType
  depth: SliceDepth
  scope: SliceScope
}

export interface TreeNode {
  key: string
  title: string
  entryType: string
  depth: number
  views: number
  watchSeconds: number
  /**
   * ⚠ NULL ON A SYNTHETIC NODE, ALWAYS. Rank is computed on the server, platform-wide and per
   * level (a leaf among leaves, a series among series). A level we invent on the client has no
   * level to be ranked within, and a rank summed from children would be a number we made up
   * about other people's content. Figures yes, position no.
   */
  rank: number | null
  rankOf: number
  rankScope: string
  synthetic: boolean
  children: TreeNode[]
}

function nodeOf(row: TitleRow): TreeNode {
  return {
    key: row.ContentKey,
    title: row.Title,
    entryType: row.EntryType,
    depth: row.Depth,
    views: row.Views,
    watchSeconds: row.WatchSeconds,
    rank: row.Rank,
    rankOf: row.RankOf,
    rankScope: row.RankScope,
    synthetic: false,
    children: [],
  }
}

/**
 * Build the navigable tree, inserting the synthetic "Supporting material" grouping node that the
 * payload does not contain but v1's menu does.
 *
 * ★★ David, 2026-07-23: "each piece of supporting material needs to be able to be analysed
 * individually … the displayed data needs to display overall for all support material and then
 * for each one individually." So the group is a real destination with its own total, AND every
 * item under it keeps its own figures. Both, not either.
 *
 * ⚠ SUPPORTING MATERIAL WITH NO PARENT GROUPS AT THE ROOT. Bailey's podcasts arrive as top-level
 * rows while Keshet's trailer arrives under its series, and both are legitimate — so the grouping
 * is by whatever parent the row declares, including none. Assuming a parent would silently drop
 * every one of Bailey's five.
 */
export function buildTree(rows: readonly TitleRow[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>()
  for (const row of rows) nodes.set(row.ContentKey, nodeOf(row))

  const roots: TreeNode[] = []
  const groups = new Map<string, TreeNode>()

  const groupFor = (parentKey: string): TreeNode => {
    const key = supportingGroupKey(parentKey)
    let group = groups.get(key)
    if (!group) {
      const parent = parentKey === '' ? null : (nodes.get(parentKey) ?? null)
      group = {
        key,
        title: 'Supporting material',
        entryType: 'SupportingMaterial',
        depth: parent ? parent.depth + 1 : 0,
        views: 0,
        watchSeconds: 0,
        rank: null,
        rankOf: 0,
        rankScope: '',
        synthetic: true,
        children: [],
      }
      groups.set(key, group)
      if (parent) parent.children.push(group)
      else roots.push(group)
    }
    return group
  }

  for (const row of rows) {
    const node = nodes.get(row.ContentKey)
    if (!node) continue
    if (isSupporting(row.EntryType)) {
      const group = groupFor(row.ParentKey)
      group.children.push(node)
      // ⚠ Sum the CHILDREN'S OWN figures. Sibling subtrees are disjoint, so this cannot double
      // count — and it matches how the server rolls a container up, including the fact that a
      // container's "views" are episode-views rather than distinct viewers.
      group.views += node.views
      group.watchSeconds += node.watchSeconds
      // The group sits one level above its items however deep the items claim to be.
      node.depth = group.depth + 1
      continue
    }
    const parent = row.ParentKey === '' ? null : nodes.get(row.ParentKey)
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  return roots
}

/**
 * The type axis. Filters the ROWS a ranking is drawn from; the tree is never filtered, because a
 * tree you cannot navigate to the bottom of is a broken tree, not a filtered one.
 */
export function matchesType(row: TitleRow, type: SliceType): boolean {
  if (type === 'all') return true
  if (type === 'supporting') return isSupporting(row.EntryType)
  if (type === 'films') return row.EntryType === 'Film'
  return row.EntryType === 'Series' || row.EntryType === 'Season' || row.EntryType === 'Episode'
}

/**
 * The depth axis — v1's `Series :` select, generalised.
 *
 * ★★ THIS IS NOT THE SAME QUESTION AS THE TREE. The tree answers "how did Season 1 do"; the depth
 * axis answers "which of my episodes did best", by re-levelling the ranking so every row in it is
 * the same kind of thing. Both were in v1 and both are wanted (David, 2026-07-23).
 */
export function rowsAtDepth(rows: readonly TitleRow[], depth: SliceDepth): TitleRow[] {
  switch (depth) {
    case 'episode':
      return rows.filter((r) => r.EntryType === 'Episode')
    case 'season':
      return rows.filter((r) => r.EntryType === 'Season')
    case 'supporting':
      return rows.filter((r) => isSupporting(r.EntryType))
    case 'title':
    default:
      // Top of the tree: a work with no parent. Supporting material is excluded here — it is
      // reached by its own depth — so a supplier comparing works compares works.
      return rows.filter((r) => r.ParentKey === '' && !isSupporting(r.EntryType))
  }
}

/**
 * WHICH LEVEL A RANKING COMPARES AT, for one set of axes. Type has already been applied.
 *
 * ⚠⚠ DEPTH APPLIES ONLY WHERE ITS CONTROL IS VISIBLE. `FilterBar` draws the `Series :` select
 * under "Only series" and nowhere else, so reading `axes.depth` for any other family lets a
 * control the user cannot see narrow the table. That is exactly how this broke: pick Only series
 * + Per episode, set the filter back to All content, and the page kept listing the twelve
 * episodes while the only control left on screen said "All content" (David, 2026-07-27 — "the
 * top drop downs do not work", reproduced on live as ZDF). A control you cannot see must not
 * filter, so depth is consulted for the series family and ignored everywhere else.
 *
 * ⚠⚠ AND "ALL CONTENT" MEANS ALL OF IT. Levelling `all` through `rowsAtDepth('title')` dropped
 * supporting material, which listed ONE of Bailey's eight titles under a caption reading "8 in
 * the catalogue" — the quiet-titles data loss this file's own header forbids, and unreachable by
 * any other route because the depth select never offers `supporting`. A top-level podcast is
 * reached from nowhere else; seasons and episodes still are, by the tree and by the depth axis.
 */
export function levelFor(rows: readonly TitleRow[], axes: SliceAxes): TitleRow[] {
  switch (axes.type) {
    case 'series':
      return rowsAtDepth(rows, axes.depth)
    // Both of these are already one kind of thing after the type filter: every film is a work at
    // the top of the tree, and every extra is wanted individually wherever it hangs — Bailey's
    // sit at the root, Keshet's under its series.
    case 'films':
    case 'supporting':
      return [...rows]
    case 'all':
    default:
      return rows.filter((r) => r.ParentKey === '')
  }
}

/**
 * What the ranking is comparing, in words, for the line above the table.
 *
 * ★★ THE CONTROL MUST BE SEEN TO WORK. No supplier in the real data has both series and films
 * (David, 2026-07-23), so "All content" and "Only series" legitimately produce the SAME rows for
 * ZDF and All3, and "All content" and "Only films" the same row for Bailey. That is honest, but
 * from the user's chair a select that reorders nothing is a select that is broken. Naming the
 * level makes the answer change even when the rows do not.
 */
export function sliceCaption(axes: SliceAxes, count: number): string {
  const one = count === 1
  const n = count.toLocaleString()
  if (axes.type === 'supporting') return `${n} ${one ? 'piece' : 'pieces'} of supporting material`
  if (axes.type === 'films') return `${n} ${one ? 'film' : 'films'}`
  if (axes.type === 'series') {
    if (axes.depth === 'episode') return `${n} ${one ? 'episode' : 'episodes'}`
    if (axes.depth === 'season') return `${n} ${one ? 'season' : 'seasons'}`
    return `${n} series`
  }
  return `${n} top-level ${one ? 'title' : 'titles'}`
}

/**
 * ★★ THE SCOPE AXIS, HONESTLY. v1's "Whole Yoreflix platform" tab drew every other supplier's
 * bar unlabelled at 20% opacity, which leaks the shape and magnitude of content that is not
 * yours. We refuse that (plan D3, enforced by the service having no method that can return it).
 *
 * What survives is the part that was actually useful: the DENOMINATOR. The same rows, shown as a
 * share of your own total or as a share of everything watched on Yoreflix. No other supplier is
 * named, counted separately, or drawn.
 */
export function shareOf(row: TitleRow, scope: SliceScope, platformWatchSeconds: number): number | null {
  if (scope === 'yours') return row.ShareOfYoursPercent
  if (platformWatchSeconds <= 0) return null
  return Math.round((row.WatchSeconds / platformWatchSeconds) * 1000) / 10
}

/** Rank a slice by a metric, for the ranking charts. Unwatched rows sort last and stay unranked. */
export function rankSlice(rows: readonly TitleRow[], metric: 'views' | 'watch'): TitleRow[] {
  const value = (r: TitleRow) => (metric === 'views' ? r.Views : r.WatchSeconds)
  return [...rows].sort((a, b) => value(b) - value(a) || a.Title.localeCompare(b.Title))
}

/** Everything a ranking chart needs, for one set of axes. */
export function slice(
  rows: readonly TitleRow[],
  axes: SliceAxes,
  metric: 'views' | 'watch',
): TitleRow[] {
  // ⚠ TYPE FIRST, THEN LEVEL. Level narrows to a rung; type narrows to a family. Applied the
  // other way round, "films at episode depth" would quietly return every episode on the account.
  const typed = rows.filter((r) => matchesType(r, axes.type))
  return rankSlice(levelFor(typed, axes), metric)
}
